import { Component, OnInit } from "@angular/core";

import { NavigationMenuItemMain } from ".";
import * as gatewayIcon from "../../assets/svg/gatewayDashboardIcon.svg";
import { GatewaysService } from "../administrative/gateways/gateways.service";
import { SnackbarService } from "../common/snackbar/snackbar.service";
import { AccountInfoService } from "../common/account-info/account-info-service";

interface NavigationData {
	menuItems: NavigationMenuItemMain[];
	sessionsMenuItem: NavigationMenuItemMain;
	gatewayMenuItem: NavigationMenuItemMain;
}

@Component({
	templateUrl: "./dashboard.component.html",
})
export class DashboardComponent implements OnInit {
	public readonly rotateIcon = { type: "material", name: "chevron_left", cssClass: "sdp-rotate-icon" };

	public isExpanded = true;
	public isDataLoaded = false;
	public data: NavigationMenuItemMain[] = [];
	private isGroupsEnabled = false;

	constructor(
		private gatewayService: GatewaysService,
		private snackbarService: SnackbarService,
		private accountInfoService: AccountInfoService
	) { }

	public async ngOnInit() {
		try {
			/*
			the double assignement to this.data is intentional
			the first assignment doesn't do async, and will set up the initial state of the data so that the component can be rendered
			the second assignment uses await, which will defer the remainder of the execution of this method until that network io completes
			during that time angular is free to perform normal event handling
			when the data object is fully created we'll assign back to this.data so that the component can be re-rendered
			*/
			this.data = this.createBaseNavigationData().menuItems;
			// make sure to wait for all state to finish loading before rendering
			const [data] = await Promise.all([
				this.getData(),
				this.gatewayService.checkForPendingGateway()
			]);
			this.data = data.menuItems;

			if (this.isGroupsEnabled) {
				// Place groups after Users
				const index = this.data.findIndex((menuItem) => {
					return menuItem.name === "dashboard.users";
				});
				this.data.splice(index + 1, 0, addGroupsToNavigation());
			}

			this.isDataLoaded = true;
		} catch (e) {
			this.snackbarService.showErrorBar(e);
		}
	}

	public setExpanded() {
		this.isExpanded = !this.isExpanded;
	}

	private createBaseNavigationData(): NavigationData {

		const result: NavigationMenuItemMain[] = [];

		result.push({ name: "dashboard.title", url: "/administration/home", icon: { type: "material", name: "dashboard" } });
		result.push({ name: "dashboard.users", url: "/administration/users", icon: { type: "material", name: "people" } });
		result.push({ name: "dashboard.applications", url: "/administration/applications", icon: { type: "material", name: "lock_open" } });
		result.push({ name: "dashboard.policies", url: "/administration/policies", icon: { type: "material", name: "assignment_turned_in" } });

		const sessionsMenuItem: NavigationMenuItemMain = {
			name: "dashboard.sessions",
			url: "/administration/sessions",
			icon: { type: "material", name: "timeline" },
			children: [
				{
					name: "sdp.label.all",
					url: "/administration/sessions/all"
				}, {
					name: "sdp.label.active",
					url: "/administration/sessions/active"
				}, {
					name: "sdp.label.inactive",
					url: "/administration/sessions/inactive"
				}
			]
		};
		result.push(sessionsMenuItem);

		const gatewayMenuItem: NavigationMenuItemMain = {
			name: "gateways.title",
			url: "/administration/gateways",
			icon: { type: "image", name: "gateway", src: gatewayIcon, cssClass: "sdp-navigation-gateway-img-icon" },
			children: [
				{
					name: "sdp.label.setup",
					url: "/administration/gateways/setup"
				}
			],
			eventRegistration: (item) => {
				// we need to listen to events that happen in the gateway area.
				// depending on the data, we will eitehr show this icon or hide it
				this.gatewayService.hasPending.subscribe((data: boolean) => {
					if (data) {
						item.statusIcon = {
							name: "info",
							type: "material",
							cssClass: "sdp-device-list-item-info",
							toolTip: "gateway.has.pending.toolTip"
						};
					} else {
						item.statusIcon = undefined;
					}
				});
			}
		};
		result.push(gatewayMenuItem);
		result.push({
			name: "dashboard.clients",
			url: "/administration/clients",
			icon: {
				type: "fa",
				name: "download",
				cssClass: "sdp-dashboard-client-access-icon"
			}
		});

		return {
			menuItems: result,
			sessionsMenuItem: sessionsMenuItem,
			gatewayMenuItem: gatewayMenuItem
		};
	}

	private async getData(): Promise<NavigationData> {
		const result = this.createBaseNavigationData();

		try {
			const accountInfo = await this.accountInfoService.getAccountInfo();
			this.isGroupsEnabled = accountInfo.limits.isGroupsEnabled;
			if (accountInfo.limits.isPrivateGatewayEnabled) {
				result.gatewayMenuItem.children!.push({
					name: "sdp.label.download",
					url: "/administration/gateways/download"
				});
			}
		} catch {
			// error handling already happened in the service
		}

		return result;
	}

}

function addGroupsToNavigation() {
	const groupsMenuItem: NavigationMenuItemMain = {
		name: "sdp.label.saml-alias",
		url: "/administration/samlalias",
		icon: { type: "fa", name: "users", cssClass: "sdp-dashboard-saml-groups-icon" }
	};
	return groupsMenuItem;
}
